/* Frenly v2.82 bag extras (1 Nov 2026).
   B2: fills the "Add another Pop" row (snippets/frenly-bag-pop-row.liquid) from
       sections/frenly-bag-pop-picks.liquid, and its one tap Add (POST /cart/add.js, then the drawer's own
       refresh, or a reload on the cart page, the same as the theme's other bag add-ons).
   B3: "Save this bag for later" consent check and the return after the customer form posts.
   Loaded from snippets/frenly-cart-drawer.liquid and sections/main-cart-footer.liquid; runs once.
   No third party code. At most 3 small section requests per row, cached for the page. */
(() => {
  'use strict';

  if (window.__frenlyBagExtras) return;
  window.__frenlyBagExtras = true;

  const MAX_CARDS = 3;
  const MAX_TAG_LISTS = 2;
  const PICKS_SECTION = 'frenly-bag-pop-picks';
  const SAVE_BAG_HASHES = ['#FbSaveBag', '#FbSaveBagPage'];
  const POP_SKIP = ['keychain', 'funko soda', 'bitty pop', 'protector', 'pop! tee', 'pop tee', 'pop & tee'];
  const cardCache = new Map();
  let rowObserver = null;

  const normalizePath = (value) => {
    try {
      const path = new URL(value, window.location.origin).pathname;
      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    } catch (_error) {
      return '';
    }
  };

  const isPopItem = (item) => {
    const text = `${item.product_title || ''} ${item.product_type || ''}`.toLowerCase();
    const pop = item.product_type === 'Funko Pop!' || text.includes('funko pop') || text.includes('pop!');
    return pop && !POP_SKIP.some((word) => text.includes(word));
  };

  const money = (cents) => `$${(Math.max(0, cents) / 100).toFixed(2)}`;

  /* ---------------------------------------------------------------- B2 row */

  const picksUrl = (base, tag) => {
    const url = new URL(tag ? `${base}/${encodeURIComponent(tag)}` : base, window.location.origin);
    url.searchParams.set('sort_by', 'best-selling');
    // Storefront filters cannot be combined with a tag path, so the in stock filter is only on the best
    // sellers list. The section skips anything sold out either way.
    if (!tag) url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('section_id', PICKS_SECTION);
    return url.toString();
  };

  const fetchCards = (url) => {
    if (!cardCache.has(url)) {
      const request = fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (!html) return [];
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.from(doc.querySelectorAll('[data-fb-pop-card]'))
            .slice(0, 6)
            .map((card) => ({ id: card.dataset.productId || '', html: card.outerHTML }));
        })
        .catch(() => {
          cardCache.delete(url);
          return [];
        });
      cardCache.set(url, request);
    }
    return cardCache.get(url);
  };

  const fillRow = async (row) => {
    if (row.dataset.fbPopState) return;
    row.dataset.fbPopState = 'loading';

    const rail = row.querySelector('[data-fb-pop-rail]');
    const base = row.dataset.picksUrl;
    if (!rail || !base) {
      row.hidden = true;
      return;
    }

    const exclude = new Set((row.dataset.exclude || '').split('|').filter(Boolean));
    const tags = Array.from(new Set((row.dataset.tags || '').split('|').filter(Boolean))).slice(0, MAX_TAG_LISTS);
    const urls = tags.map((tag) => picksUrl(base, tag)).concat(picksUrl(base, ''));
    const picked = [];

    for (const url of urls) {
      if (picked.length >= MAX_CARDS) break;
      // eslint-disable-next-line no-await-in-loop
      const cards = await fetchCards(url);
      for (const card of cards) {
        if (picked.length >= MAX_CARDS) break;
        if (!card.id || exclude.has(card.id)) continue;
        exclude.add(card.id);
        picked.push(card);
      }
    }

    if (!row.isConnected) return;
    row.dataset.fbPopState = 'done';
    rail.removeAttribute('aria-busy');
    if (!picked.length) {
      row.hidden = true;
      return;
    }
    rail.innerHTML = picked.map((card) => card.html).join('');
  };

  const watchRows = (root) => {
    (root || document).querySelectorAll('[data-fb-pop-row]').forEach((row) => {
      if (row.dataset.fbPopWatched) return;
      row.dataset.fbPopWatched = 'true';
      // In the bag the cards come from the same cached lists, so fill at once while the bag is open: on phones the row sat below the
      // drawer's fold after an Add and showed grey placeholders until scrolled (bag walk, 1 Nov 2026).
      const openBag = row.closest('cart-drawer[data-open="true"]');
      if (rowObserver && !openBag) rowObserver.observe(row);
      else fillRow(row);
    });
  };

  if ('IntersectionObserver' in window) {
    rowObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          rowObserver.unobserve(entry.target);
          fillRow(entry.target);
        });
      },
      { rootMargin: '0px 0px 240px 0px' }
    );
  }

  const announce = (row, message) => {
    const live = row && row.querySelector('[data-fb-pop-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 50);
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-fb-pop-add]')) return;
      event.preventDefault();
      event.stopPropagation();

      const button = form.querySelector('button[type="submit"]');
      const idInput = form.querySelector('input[name="id"]');
      const variantId = Number(idInput && idInput.value);
      if (!button || button.disabled || !variantId) return;

      const row = form.closest('[data-fb-pop-row]');
      const card = form.closest('[data-fb-pop-card]');
      const cartUrl = (row && row.dataset.cartUrl) || '/cart';
      const addUrl = `${(form.getAttribute('action') || '/cart/add').replace(/\.js$/, '')}.js`;

      button.disabled = true;
      button.textContent = 'Adding';

      // B2 fix (1 Nov 2026): ask for the drawer's sections in the same add call (Shopify Section Rendering),
      // so the bag re-renders from the add's own answer, not a second read that may come back stale.
      const drawerEl = document.querySelector('cart-drawer');
      const addBody = { items: [{ id: variantId, quantity: 1 }] };
      if (drawerEl && typeof drawerEl.getSectionsToRender === 'function') {
        try {
          addBody.sections = drawerEl.getSectionsToRender().map((section) => section.id).join(',');
          addBody.sections_url = window.location.pathname;
        } catch (_error) {
          /* no sections, the refresh below still runs */
        }
      }
      let addPayload = {};

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(addBody),
        });
        let payload = {};
        try {
          payload = await response.json();
        } catch (_error) {
          payload = {};
        }
        addPayload = payload;
        if (!response.ok || payload.status) {
          const error = new Error(payload.description || payload.message || 'Add failed');
          error.status = response.status || payload.status;
          throw error;
        }
      } catch (error) {
        const unavailable = Number(error.status) === 422;
        button.textContent = unavailable ? 'Sold out' : 'Try again';
        button.disabled = unavailable;
        announce(row, unavailable ? 'Sorry, that Pop is not available now.' : 'That Pop was not added. Please try again.');
        return;
      }

      button.textContent = 'Added';
      announce(row, 'Added to your bag.');
      document.dispatchEvent(
        new CustomEvent('frenly:commerce', {
          detail: { event: 'addon_add', component: 'bag_pop_row', product_id: card ? card.dataset.productId : null },
        })
      );

      // Same rule as the drawer's own add-on path: on the cart page the page's sections have template ids, so
      // reload; elsewhere re-render the drawer with its own refresh.
      if (normalizePath(window.location.pathname) === normalizePath(cartUrl)) {
        window.location.reload();
        return;
      }
      let rendered = false;
      if (drawerEl && typeof drawerEl.renderSections === 'function' && addPayload && addPayload.sections) {
        try {
          rendered = await drawerEl.renderSections(addPayload.sections, 'drawer-close');
        } catch (_error) {
          rendered = false;
        }
      }
      const drawer = window.FrenlyCartDrawer;
      if (!rendered && drawer && typeof drawer.refresh === 'function') {
        try {
          rendered = await drawer.refresh();
        } catch (_error) {
          rendered = false;
        }
      }
      // A real re-render replaces the drawer's contents, so this Add button leaves the page. If it is still here,
      // the bag did not update: reload so the count, subtotal and checkout total are never stale.
      if (!rendered || button.isConnected) window.location.reload();
    },
    true
  );

  // Cart page: Dawn re-renders only the footer's .js-contents on a quantity change, so the row (outside it)
  // re-checks the real cart: hide it when no Pop is left, drop a card now in the bag, and recompute the free
  // shipping line from the cart total. On any read error the shipping line is hidden, never guessed.
  const syncPageRow = async () => {
    const row = document.querySelector('#main-cart-footer [data-fb-pop-row]');
    if (!row) return;
    const ship = row.querySelector('[data-fb-pop-ship]');
    try {
      const response = await fetch(`${row.dataset.cartUrl || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('cart read failed');
      const cart = await response.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.some(isPopItem)) {
        row.hidden = true;
        return;
      }
      const inBag = new Set(items.map((item) => String(item.product_id)));
      row.querySelectorAll('[data-fb-pop-card]').forEach((card) => {
        if (inBag.has(card.dataset.productId)) card.remove();
      });
      if (row.dataset.fbPopState === 'done' && !row.querySelector('[data-fb-pop-card]')) row.hidden = true;
      if (ship) {
        const threshold = Number(row.dataset.threshold) || 0;
        const remaining = threshold - Number(cart.total_price || 0);
        const show = row.dataset.shipAu === 'true' && cart.currency === 'AUD' && threshold > 0 && remaining > 0;
        ship.textContent = show ? `Add ${money(remaining)} more for free shipping` : '';
        ship.hidden = !show;
      }
    } catch (_error) {
      if (ship) {
        ship.textContent = '';
        ship.hidden = true;
      }
    }
  };

  const watchCartPage = () => {
    const contents = document.querySelector('#main-cart-footer .js-contents');
    if (!contents || !('MutationObserver' in window)) return;
    let timer = null;
    new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(syncPageRow, 250);
    }).observe(contents, { childList: true });
  };

  /* ----------------------------------------------------------- B3 save bag */

  const consentError = (form, show) => {
    const error = form && form.querySelector('[data-fb-savebag-err]');
    const box = form && form.querySelector('[data-fb-savebag-consent]');
    if (error) error.hidden = !show;
    if (box) {
      if (show) box.setAttribute('aria-invalid', 'true');
      else box.removeAttribute('aria-invalid');
    }
  };

  // The box is `required`, so the browser stops the post. Show our own words instead of the browser bubble.
  document.addEventListener(
    'invalid',
    (event) => {
      const box = event.target;
      if (!(box instanceof HTMLInputElement) || !box.matches('[data-fb-savebag-consent]')) return;
      event.preventDefault();
      const form = box.closest('form');
      consentError(form, true);
      const email = form && form.querySelector('input[type="email"]');
      if (email && !email.checkValidity()) return;
      box.focus();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('fb-savebag')) return;
      const box = form.querySelector('[data-fb-savebag-consent]');
      if (box && !box.checked) {
        event.preventDefault();
        consentError(form, true);
        box.focus();
      }
    },
    true
  );

  document.addEventListener('change', (event) => {
    const box = event.target;
    if (box instanceof HTMLInputElement && box.matches('[data-fb-savebag-consent]') && box.checked) {
      consentError(box.closest('form'), false);
    }
  });

  const handleSaveBagReturn = () => {
    const posted = /[?&]customer_posted=true(?:&|$)/.test(window.location.search);
    const ours = SAVE_BAG_HASHES.includes(window.location.hash);

    document.querySelectorAll('form.fb-savebag').forEach((form) => {
      const ok = form.querySelector('[data-fb-savebag-ok]');
      const fields = form.querySelector('[data-fb-savebag-fields]');
      const fold = form.querySelector('[data-fb-savebag]');
      if (posted && ours) {
        if (ok) ok.hidden = false;
        if (fields) fields.hidden = true;
        if (fold) fold.open = true;
      } else if (posted && !ours) {
        // Another customer form on the page (the footer newsletter) posted, not this one.
        if (ok) ok.hidden = true;
        if (fields) fields.hidden = false;
        const serverError = form.querySelector('[data-fb-savebag-server-err]');
        if (serverError) serverError.hidden = true;
      }
    });

    if (window.location.hash === '#FbSaveBag' && window.customElements) {
      window.customElements.whenDefined('cart-drawer').then(() => {
        const drawer = window.FrenlyCartDrawer;
        if (drawer && typeof drawer.open === 'function') drawer.open(document.activeElement, { loadRecommendations: true });
      });
    }
  };

  /* ----------------------------------------------------------------- start */

  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;
    // The drawer swaps its children on every bag change; pick up the new row each time.
    new MutationObserver(() => watchRows(drawer)).observe(drawer, { childList: true });
  };

  const start = () => {
    watchRows(document);
    watchDrawer();
    watchCartPage();
    handleSaveBagReturn();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
